import { useCallback, useEffect, useRef, useState } from "react";
import {
  DETECTOR_LIMITS,
  advanceDetector,
  beginCooldown,
  beginManualCapture,
  initialDetectorState,
  poseSampleFromLandmarks,
} from "../lib/swingDetector";
import { preRollTrimStartMs, recordingStopDelayMs } from "../lib/cameraTiming";
import { prepareCameraClip } from "../lib/cameraClipProcessor";

const FRAME_INTERVAL_MS = 1000 / 15;
const MANUAL_CAPTURE_MS = DETECTOR_LIMITS.recordingTimeoutMs;

const selectRecorderMimeType = () => {
  const candidates = [
    "video/mp4;codecs=avc1.42E01E",
    "video/mp4",
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
  ];
  return candidates.find((type) => window.MediaRecorder?.isTypeSupported(type)) || "";
};

const baseContentType = (mimeType) => mimeType.split(";", 1)[0].toLowerCase();

export const useSwingCamera = ({
  enabled,
  paused,
  onCapture,
  maxClipBytes,
  maxClipDurationMs = DETECTOR_LIMITS.recordingTimeoutMs,
}) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const workerRef = useRef(null);
  const detectorReadyRef = useRef(false);
  const frameTimerRef = useRef(null);
  const framePendingRef = useRef(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recordingStartedRef = useRef(null);
  const swingStartedRef = useRef(null);
  const discardRecordingRef = useRef(false);
  const captureMethodRef = useRef("automatic");
  const manualTimerRef = useRef(null);
  const finalizingRef = useRef(false);
  const detectorStateRef = useRef(initialDetectorState());
  const previousSampleRef = useRef(null);
  const pausedRef = useRef(paused);
  const mountedRef = useRef(true);

  const [cameraStatus, setCameraStatus] = useState("idle");
  const [detectorStatus, setDetectorStatus] = useState("loading");
  const [capturePhase, setCapturePhase] = useState("positioning");
  const [poseStatus, setPoseStatus] = useState("Position yourself inside the guide");
  const [poses, setPoses] = useState([]);
  const [poseValid, setPoseValid] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [streamSettings, setStreamSettings] = useState(null);

  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  const stopRecorder = useCallback((discard = false) => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === "inactive") return;
    discardRecordingRef.current = discard;
    if (manualTimerRef.current) clearTimeout(manualTimerRef.current);
    manualTimerRef.current = null;
    recorder.stop();
  }, []);

  const startRecorder = useCallback((captureMethod = "automatic") => {
    if (pausedRef.current || !streamRef.current || recorderRef.current?.state === "recording") return;
    const mimeType = selectRecorderMimeType();
    if (!window.MediaRecorder || !mimeType) {
      setError("This browser cannot record a supported camera format. Use current Chrome or Edge.");
      return;
    }

    chunksRef.current = [];
    discardRecordingRef.current = false;
    captureMethodRef.current = captureMethod;
    recordingStartedRef.current = performance.now();
    swingStartedRef.current = null;
    const recorder = new window.MediaRecorder(streamRef.current, {
      mimeType,
      videoBitsPerSecond: 3_000_000,
    });
    recorderRef.current = recorder;
    recorder.ondataavailable = ({ data }) => {
      if (data?.size) chunksRef.current.push(data);
    };
    recorder.onerror = ({ error: recorderError }) => {
      setError(recorderError?.message || "Camera recording failed.");
      discardRecordingRef.current = true;
    };
    recorder.onstop = async () => {
      const recordingStartedAt = recordingStartedRef.current;
      const swingStartedAt = swingStartedRef.current;
      const durationMs = Math.round(performance.now() - recordingStartedAt);
      const blob = new Blob(chunksRef.current, { type: baseContentType(recorder.mimeType) });
      const contentType = baseContentType(recorder.mimeType);
      const captureMethodAtStop = captureMethodRef.current;
      const trackSettings = streamRef.current?.getVideoTracks()[0]?.getSettings() || {};
      const width = trackSettings.width || videoRef.current?.videoWidth || 1280;
      const height = trackSettings.height || videoRef.current?.videoHeight || 720;
      const fps = trackSettings.frameRate || 30;
      recorderRef.current = null;
      chunksRef.current = [];
      recordingStartedRef.current = null;
      swingStartedRef.current = null;
      detectorStateRef.current = beginCooldown(performance.now());

      if (discardRecordingRef.current) {
        if (mountedRef.current) setCapturePhase("cooldown");
        return;
      }

      finalizingRef.current = true;
      if (mountedRef.current) setCapturePhase("validating");
      try {
        if (durationMs < 1000 || blob.size === 0) {
          throw new Error("The detected clip was incomplete.");
        }
        const trimStartMs = preRollTrimStartMs(recordingStartedAt, swingStartedAt);
        const prepared = await prepareCameraClip({
          blob,
          contentType,
          durationMs,
          width,
          height,
          fps,
          trimStartMs,
          transcode: captureMethodAtStop === "automatic" && trimStartMs !== null,
          maxClipBytes,
          maxClipDurationMs,
        });
        if (!mountedRef.current) return;
        setWarning(prepared.warning);
        setError(null);
        onCapture({
          blob: prepared.blob,
          contentType: prepared.contentType,
          durationMs: prepared.durationMs,
          width: prepared.width,
          height: prepared.height,
          fps: prepared.fps,
          captureMethod: captureMethodAtStop,
        });
      } catch (processingError) {
        if (mountedRef.current) {
          setError(`${processingError?.message || "The camera clip could not be prepared."} Please record the swing again.`);
        }
      } finally {
        finalizingRef.current = false;
        if (mountedRef.current) setCapturePhase("cooldown");
      }
    };
    recorder.start(250);
    setCapturePhase("recording");
    setError(null);
    setWarning(null);

    const requestedDuration = captureMethod === "manual"
      ? MANUAL_CAPTURE_MS
      : DETECTOR_LIMITS.recordingTimeoutMs;
    const maximumDuration = recordingStopDelayMs(requestedDuration, maxClipDurationMs);
    manualTimerRef.current = setTimeout(() => {
      const discard = captureMethod === "automatic" && !detectorStateRef.current.swingSeen;
      stopRecorder(discard);
    }, maximumDuration);
  }, [maxClipBytes, maxClipDurationMs, onCapture, stopRecorder]);

  const handleDetectorResult = useCallback(({ landmarks, timestamp }) => {
    framePendingRef.current = false;
    const sample = poseSampleFromLandmarks(landmarks, previousSampleRef.current, timestamp);
    if (sample.valid) previousSampleRef.current = sample;
    setPoses(landmarks || []);
    setPoseValid(sample.valid);
    if (!sample.valid) {
      if (sample.poseCount > 1) {
        setPoseStatus("Multiple people detected — keep only the golfer in frame");
      } else if (sample.poseCount === 0) {
        setPoseStatus("No golfer detected — improve framing or lighting");
      } else {
        const missing = sample.missingJoints.slice(0, 3).join(", ");
        const remainder = sample.missingJoints.length > 3 ? " and other joints" : "";
        setPoseStatus(`Keep ${missing}${remainder} visible`);
      }
    } else if (!sample.address) {
      const partial = sample.trackingQuality === "partial"
        ? ` — ${sample.missingJoints[0]} intermittent but tolerated`
        : "";
      setPoseStatus(`Pose tracked — move into your address position${partial}`);
    } else if (sample.motion <= DETECTOR_LIMITS.motionStopThreshold) {
      const partial = sample.trackingQuality === "partial"
        ? `; ${sample.missingJoints[0]} intermittent but tolerated`
        : "";
      setPoseStatus(`Address detected — hold still while camera arms${partial}`);
    } else {
      setPoseStatus("Swing motion detected");
    }

    if (pausedRef.current || finalizingRef.current) return;
    const advanced = advanceDetector(detectorStateRef.current, sample, timestamp);
    detectorStateRef.current = advanced.state;
    setCapturePhase(advanced.state.phase);
    advanced.actions.forEach((action) => {
      if (action === "start_recording") startRecorder("automatic");
      if (action === "swing_started" && swingStartedRef.current === null) {
        swingStartedRef.current = timestamp;
      }
      if (action === "stop_recording") stopRecorder(false);
      if (action === "abort_recording") {
        stopRecorder(captureMethodRef.current !== "manual");
      }
    });
  }, [startRecorder, stopRecorder]);

  const stopCamera = useCallback(() => {
    if (frameTimerRef.current) clearInterval(frameTimerRef.current);
    frameTimerRef.current = null;
    if (manualTimerRef.current) clearTimeout(manualTimerRef.current);
    manualTimerRef.current = null;
    stopRecorder(true);
    workerRef.current?.postMessage({ type: "close" });
    workerRef.current?.terminate();
    workerRef.current = null;
    detectorReadyRef.current = false;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    previousSampleRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setPoses([]);
    setPoseValid(false);
    setCameraStatus("idle");
  }, [stopRecorder]);

  const startCamera = useCallback(async () => {
    stopCamera();
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Camera access is unavailable. Open this page over HTTPS in current Chrome or Edge.");
      setCameraStatus("error");
      return;
    }
    setCameraStatus("requesting");
    setDetectorStatus("loading");
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 60, min: 30 },
          facingMode: "user",
        },
      });
      if (!mountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      const track = stream.getVideoTracks()[0];
      track.onended = () => {
        setError("The camera was disconnected. Reconnect it and restart the camera.");
        setCameraStatus("error");
      };
      setStreamSettings(track.getSettings());
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setCameraStatus("ready");

      const worker = new Worker(new URL("../workers/pose.worker.js", import.meta.url));
      workerRef.current = worker;
      worker.onmessage = ({ data }) => {
        if (data.type === "ready") {
          detectorReadyRef.current = true;
          setDetectorStatus("ready");
          return;
        }
        if (data.type === "result") {
          handleDetectorResult(data);
          return;
        }
        if (data.type === "error") {
          framePendingRef.current = false;
          detectorReadyRef.current = false;
          setDetectorStatus("error");
          setError(`${data.message} Automatic detection is unavailable; use Record next swing.`);
        }
      };
      worker.onerror = () => {
        framePendingRef.current = false;
        detectorReadyRef.current = false;
        setDetectorStatus("error");
        setError("Automatic swing detection stopped. Use Record next swing.");
      };
      worker.postMessage({
        type: "init",
        wasmBaseUrl: `${window.location.origin}/vendor/mediapipe`,
        modelUrl: process.env.REACT_APP_POSE_MODEL_URL,
      });

      frameTimerRef.current = setInterval(async () => {
        if (
          framePendingRef.current
          || !detectorReadyRef.current
          || pausedRef.current
          || finalizingRef.current
          || workerRef.current !== worker
          || workerRef.current === null
          || videoRef.current?.readyState < 2
        ) return;
        framePendingRef.current = true;
        try {
          const bitmap = await createImageBitmap(videoRef.current);
          worker.postMessage({ type: "frame", bitmap, timestamp: performance.now() }, [bitmap]);
        } catch (frameError) {
          framePendingRef.current = false;
          setError(frameError?.message || "Could not read a camera frame.");
        }
      }, FRAME_INTERVAL_MS);
    } catch (cameraError) {
      const permissionDenied = cameraError?.name === "NotAllowedError";
      setError(permissionDenied
        ? "Camera permission was denied. Allow access in the browser, then try again."
        : cameraError?.message || "Could not start the camera.");
      setCameraStatus("error");
    }
  }, [handleDetectorResult, stopCamera]);

  const recordManually = useCallback(() => {
    if (cameraStatus !== "ready" || paused || finalizingRef.current) return;
    detectorStateRef.current = beginManualCapture(performance.now());
    setCapturePhase("recording");
    startRecorder("manual");
  }, [cameraStatus, paused, startRecorder]);

  useEffect(() => {
    mountedRef.current = true;
    if (enabled) startCamera();
    return () => {
      mountedRef.current = false;
      stopCamera();
    };
  }, [enabled, startCamera, stopCamera]);

  useEffect(() => {
    if (paused) {
      stopRecorder(true);
      detectorStateRef.current = initialDetectorState();
      setCapturePhase("positioning");
    }
  }, [paused, stopRecorder]);

  return {
    videoRef,
    cameraStatus,
    detectorStatus,
    capturePhase,
    poseStatus,
    poses,
    poseValid,
    streamSettings,
    error,
    warning,
    startCamera,
    stopCamera,
    recordManually,
  };
};
